import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@parrot-software/design-system';

import useOrganization from 'hooks/Organization/useOrganization';
import { useGetOrganizationConfig } from 'hooks/Organization/useOrganizationConfig';

import { PERMISSIONS_UI_TREE } from '@personnel/presentation/config/permissionsUITree';
import { PERMISSIONS } from '@personnel/domain/types/PermissionsConst';
import type { FrontendPermissionId, PermissionLevel as PermissionLevelType } from '@personnel/domain';
import type {
  PermissionNode,
  PermissionContainer,
  PermissionSection as PermissionSectionType,
  PermissionTab,
} from '@personnel/presentation/config/permissionsUITree.types';

import { PermissionSwitch } from './PermissionSwitch';
import { PermissionCheckbox } from './PermissionCheckbox';
import { PermissionGroup } from './PermissionGroup';
import { PermissionAccordion } from './PermissionAccordion';
import { PermissionModule } from './PermissionModule';
import { PermissionSection } from './PermissionSection';

/**
 * Permissions Renderer Component
 *
 * Orchestrator component that reads the UI Tree and renders the complete permission hierarchy.
 * Manages state and propagates changes to the parent form.
 * Uses i18n for all text content.
 *
 * @param {PermissionsRendererProps} props - Component properties
 * @returns {React.ReactElement} Permissions renderer component
 *
 * @example
 * <PermissionsRenderer
 *   level="store"
 *   selectedPermissions={['store-pos-access', 'org-menu']}
 *   onChange={(permissions) => console.log(permissions)}
 * />
 */

interface PermissionsRendererProps {
  /** Permission level (organization or store) */
  level: PermissionLevelType;
  /** Currently selected permission IDs */
  selectedPermissions: FrontendPermissionId[];
  /** Callback when permissions change */
  onChange: (permissions: FrontendPermissionId[]) => void;
  /** Whether all permission controls are disabled */
  disabled?: boolean;
}

export const PermissionsRenderer = ({
  level,
  selectedPermissions,
  onChange,
  disabled = false,
}: PermissionsRendererProps): React.ReactElement => {
  const { t } = useTranslation('roles');
  const [activeTab, setActiveTab] = React.useState('0');
  const { organization, currentStore } = useOrganization();

  // Get organization config from API
  const { organizationConfigData } = useGetOrganizationConfig(organization?.uuid || '', currentStore?.uuid || '');

  // Store admin permission ID (only for store level)
  const STORE_ADMIN: FrontendPermissionId = 'store-admin';

  // Check if store admin is enabled
  const isStoreAdminEnabled = level === 'store' && selectedPermissions.includes(STORE_ADMIN);

  /**
   * Determines if a permission should be rendered based on organization configuration
   *
   * @param {FrontendPermissionId} frontendId - Permission ID to validate
   * @returns {boolean} True if permission should be rendered
   */
  const shouldRenderPermission = (frontendId: FrontendPermissionId): boolean => {
    // Validate ticket editor based on organization configuration from API
    if (frontendId === 'store-portal-tickets') {
      return organizationConfigData?.allowTicketEditor === true;
    }
    return true;
  };

  /**
   * Filters permissions from a container based on organization configuration
   *
   * @param {PermissionContainer} container - Container to filter
   * @returns {PermissionContainer} Filtered container
   */
  const filterContainer = (container: PermissionContainer): PermissionContainer => {
    return {
      ...container,
      permissions: container.permissions.filter((node) => shouldRenderPermission(node.frontendId)),
    };
  };

  /**
   * Filters sections recursively based on organization configuration
   *
   * @param {PermissionSectionType[]} sections - Sections to filter
   * @returns {PermissionSectionType[]} Filtered sections
   */
  const filterSections = (sections: PermissionSectionType[]): PermissionSectionType[] => {
    return sections.map((section) => ({
      ...section,
      permissions: section.permissions?.filter((node) => shouldRenderPermission(node.frontendId)),
      containers: section.containers?.map(filterContainer),
      tabs: section.tabs?.map((tab) => ({
        ...tab,
        sections: filterSections(tab.sections),
      })),
    }));
  };

  // Get UI tree for current level and filter it based on organization config
  const uiTree = React.useMemo(() => {
    const baseTree = PERMISSIONS_UI_TREE[level];
    return {
      ...baseTree,
      sections: filterSections(baseTree.sections),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, organizationConfigData?.allowTicketEditor]);

  // Check if permission is selected
  const isPermissionSelected = (permissionId: FrontendPermissionId): boolean => {
    return selectedPermissions.includes(permissionId);
  };

  /**
   * Extracts all permission IDs from a container
   *
   * @param {PermissionContainer} container - Container to extract permissions from
   * @returns {FrontendPermissionId[]} Array of permission IDs
   */
  const extractPermissionsFromContainer = (container: PermissionContainer): FrontendPermissionId[] => {
    const permissionIds: FrontendPermissionId[] = [];

    // Add parent switch if it's a module-with-switch
    if (container.type === 'module-with-switch') {
      permissionIds.push(container.parentSwitch.frontendId);
    }

    // Add all child permissions (already filtered by uiTree)
    container.permissions.forEach((node) => {
      permissionIds.push(node.frontendId);
    });

    return permissionIds;
  };

  /**
   * Gets all permissions controlled by store admin (store-admin)
   *
   * Returns all permission IDs from all tabs and sections, excluding the store admin permission itself.
   * The uiTree is already filtered based on organization configuration.
   *
   * @returns {FrontendPermissionId[]} Array of all permission IDs under store admin control
   */
  const getAllStoreAdminControlledPermissions = (): FrontendPermissionId[] => {
    const allPermissions: FrontendPermissionId[] = [];

    // Helper function to extract permissions from sections
    const extractFromSections = (sections: PermissionSectionType[]) => {
      sections.forEach((section) => {
        // Add direct permissions in section (excluding store admin itself, already filtered by uiTree)
        section.permissions?.forEach((node) => {
          if (node.frontendId !== STORE_ADMIN) {
            allPermissions.push(node.frontendId);
          }
        });

        // Add permissions from containers (already filtered by uiTree)
        section.containers?.forEach((container) => {
          const containerPermissions = extractPermissionsFromContainer(container);
          allPermissions.push(...containerPermissions);
        });

        // Process tabs
        section.tabs?.forEach((tab) => {
          extractFromSections(tab.sections);
        });
      });
    };

    // Start extraction from root sections
    extractFromSections(uiTree.sections);

    return allPermissions;
  };

  /**
   * Finds all child permissions of a given parent permission ID
   *
   * Recursively searches through the UI tree to find all permissions that are
   * children of the specified parent permission.
   * Also handles gatekeeper permissions that control entire sections.
   *
   * @param {FrontendPermissionId} parentId - Parent permission ID
   * @returns {FrontendPermissionId[]} Array of child permission IDs
   */
  const findChildPermissions = (parentId: FrontendPermissionId): FrontendPermissionId[] => {
    const childIds: FrontendPermissionId[] = [];

    // Helper function to search through sections recursively
    const searchInSections = (sections: PermissionSectionType[]) => {
      sections.forEach((section) => {
        // Check if this permission is a gatekeeper for this section
        if (section.gatekeeperPermissionId === parentId) {
          // This is a gatekeeper! Get ALL permissions from ALL containers
          section.containers?.forEach((container) => {
            const containerPermissions = extractPermissionsFromContainer(container);
            childIds.push(...containerPermissions);
          });
        }

        // Search in containers for module-with-switch parents
        section.containers?.forEach((container) => {
          if (container.type === 'module-with-switch' && container.parentSwitch.frontendId === parentId) {
            // Found the parent module! Get all its children
            container.permissions.forEach((node) => {
              childIds.push(node.frontendId);
            });
          }
        });

        // Search in tabs
        section.tabs?.forEach((tab) => {
          searchInSections(tab.sections);
        });
      });
    };

    // Start search from root sections
    searchInSections(uiTree.sections);

    return childIds;
  };

  /**
   * Toggle permission with cascade logic
   *
   * Special cases:
   * - Store Admin (store-admin): When checked, automatically checks ALL other permissions
   * - Parent permissions: When unchecked, all children are also unchecked
   *
   * @param {FrontendPermissionId} permissionId - Permission to toggle
   * @param {boolean} checked - New checked state
   */
  const togglePermission = (permissionId: FrontendPermissionId, checked: boolean) => {
    if (checked) {
      // Special case: Store Admin enables ALL permissions
      if (permissionId === STORE_ADMIN) {
        const allControlledPermissions = getAllStoreAdminControlledPermissions();
        const allPermissions = [STORE_ADMIN, ...allControlledPermissions];
        // Remove duplicates
        const uniquePermissions = Array.from(new Set([...selectedPermissions, ...allPermissions]));
        onChange(uniquePermissions);
      } else {
        // Simply add the permission
        onChange([...selectedPermissions, permissionId]);
      }
    } else {
      // Uncheck: remove the permission AND all its children
      const childPermissions = findChildPermissions(permissionId);
      const permissionsToRemove = [permissionId, ...childPermissions];

      // Filter out the parent and all its children
      const updatedPermissions = selectedPermissions.filter((id) => !permissionsToRemove.includes(id));

      onChange(updatedPermissions);
    }
  };

  // Render a single permission node (checkbox or switch)
  const renderPermissionNode = (node: PermissionNode, nodeDisabled = false): React.ReactElement => {
    const permission = PERMISSIONS[node.frontendId];
    const isChecked = isPermissionSelected(node.frontendId);

    // Store admin logic: if store admin is enabled, all other permissions are checked and disabled
    const isStoreAdminControlled = isStoreAdminEnabled && node.frontendId !== STORE_ADMIN;
    const isNodeDisabled = disabled || nodeDisabled || isStoreAdminControlled;
    const finalChecked = isStoreAdminControlled ? true : isChecked;

    // Get translated label and description
    const label = t(`permissions.${node.frontendId}.name`);
    const description = t(`permissions.${node.frontendId}.description`);

    if (node.component === 'switch') {
      return (
        <PermissionSwitch
          backendIds={permission.primaryIds}
          checked={finalChecked}
          description={description}
          disabled={isNodeDisabled}
          key={node.frontendId}
          label={label}
          onChange={(checked) => togglePermission(node.frontendId, checked)}
          permissionId={node.frontendId}
        />
      );
    }

    // Default: checkbox
    return (
      <PermissionCheckbox
        backendIds={permission.primaryIds}
        checked={finalChecked}
        description={description}
        disabled={isNodeDisabled}
        key={node.frontendId}
        label={label}
        onChange={(checked) => togglePermission(node.frontendId, checked)}
        permissionId={node.frontendId}
      />
    );
  };

  // Render a container (module, group, or accordion)
  const renderContainer = (container: PermissionContainer, containerDisabled = false): React.ReactElement => {
    // Container-level disabled state (from gatekeeper, global disabled, or store admin)
    const isContainerDisabled = disabled || containerDisabled || isStoreAdminEnabled;

    if (container.type === 'module-with-switch') {
      const parentPermission = PERMISSIONS[container.parentSwitch.frontendId];
      const isParentChecked = isPermissionSelected(container.parentSwitch.frontendId);

      // Store admin logic: if store admin is enabled, parent switch is checked and disabled
      const finalParentChecked = isStoreAdminEnabled ? true : isParentChecked;

      // Get translated label and description for parent switch
      const parentLabel = t(`permissions.${container.parentSwitch.frontendId}.name`);
      const parentDescription = t(`permissions.${container.parentSwitch.frontendId}.description`);

      return (
        <PermissionModule
          disabled={isContainerDisabled}
          iconName={container.iconName}
          key={`module-${container.parentSwitch.frontendId}`}
          onParentChange={(checked) => togglePermission(container.parentSwitch.frontendId, checked)}
          parentBackendIds={parentPermission.primaryIds}
          parentChecked={finalParentChecked}
          parentSwitchDescription={parentDescription}
          parentSwitchId={container.parentSwitch.frontendId}
          parentSwitchLabel={parentLabel}
        >
          {container.permissions.map((node) => renderPermissionNode(node, isContainerDisabled))}
        </PermissionModule>
      );
    }

    if (container.type === 'accordion') {
      // Get translated name and description for accordion
      const name = t(`permissionsTree.groups.${container.i18nKey}.name`);
      const description = t(`permissionsTree.groups.${container.i18nKey}.description`, { defaultValue: '' });

      return (
        <PermissionAccordion
          defaultOpen={container.defaultOpen}
          description={description || undefined}
          key={`accordion-${container.i18nKey}`}
          name={name}
        >
          {container.permissions.map((node) => renderPermissionNode(node, isContainerDisabled))}
        </PermissionAccordion>
      );
    }

    // type === 'group'
    // Get translated name and description for group
    const name = t(`permissionsTree.groups.${container.i18nKey}.name`);
    const description = t(`permissionsTree.groups.${container.i18nKey}.description`, { defaultValue: '' });

    return (
      <PermissionGroup
        description={description || undefined}
        iconName={container.iconName}
        key={`group-${container.i18nKey}`}
        name={name}
      >
        {container.permissions.map((node) => renderPermissionNode(node, isContainerDisabled))}
      </PermissionGroup>
    );
  };

  // Render a section
  const renderSection = (section: PermissionSectionType): React.ReactElement => {
    // Check if gatekeeper permission controls this section's containers
    const gatekeeperEnabled = section.gatekeeperPermissionId
      ? isPermissionSelected(section.gatekeeperPermissionId as FrontendPermissionId)
      : true;

    // Containers are disabled when: globally disabled OR gatekeeper is OFF
    const containersDisabled = disabled || !gatekeeperEnabled;

    // Get translated name and description for section
    const name = t(`permissionsTree.sections.${section.i18nKey}.name`);
    const description = t(`permissionsTree.sections.${section.i18nKey}.description`, { defaultValue: '' });

    return (
      <PermissionSection description={description || undefined} key={`section-${section.i18nKey}`} name={name}>
        {/* Direct permissions (gatekeeper switch is always enabled) */}
        {section.permissions?.map((node) => renderPermissionNode(node))}

        {/* Containers with gap decorations (Chrome 139+) */}
        {section.containers && section.containers.length > 0 && (
          <div
            className={`flex flex-col gap-12 [row-rule:1px_solid_oklch(var(--oklch-border))] ${
              containersDisabled ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            {section.containers.map((container) => renderContainer(container, containersDisabled))}
          </div>
        )}

        {/* Tabs (if any) */}
        {section.tabs && renderTabs(section.tabs)}
      </PermissionSection>
    );
  };

  // Render tabs
  const renderTabs = (tabs: PermissionTab[]): React.ReactElement => {
    return (
      <Tabs onValueChange={(value) => setActiveTab(value)} value={activeTab}>
        <TabsList className="bg-muted p-1.5 rounded-md inline-flex w-fit h-auto">
          {tabs.map((tab, index) => (
            <TabsTrigger
              className="px-3 py-1.5 text-sm font-medium rounded-xs data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              key={`tab-${tab.i18nKey}`}
              value={String(index)}
            >
              {t(`permissionsTree.tabs.${tab.i18nKey}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab, index) => (
          <TabsContent className="mt-6" key={`tabpanel-${tab.i18nKey}`} value={String(index)}>
            <div className="flex flex-col gap-6">{tab.sections.map((section) => renderSection(section))}</div>
          </TabsContent>
        ))}
      </Tabs>
    );
  };

  return <div className="flex flex-col gap-6">{uiTree.sections.map((section) => renderSection(section))}</div>;
};
